import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import FileTree from '../components/portal/FileTree';
import DocumentViewer from '../components/portal/DocumentViewer';
import FloatingAssistant from '../components/portal/FloatingAssistant';

interface ClientConfig {
  id: string;
  name: string;
  fullName?: string;
  primaryColor: string;
  secondaryColor?: string;
  logo?: string;
  type?: string;
}

export default function UnifiedDashboard() {
  const { clientId } = useParams<{ clientId: string }>();
  const [config, setConfig] = useState<ClientConfig | null>(null);
  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newDocName, setNewDocName] = useState('');
  const [creating, setCreating] = useState(false);

  // Dropbox intelligente
  const [isDragging, setIsDragging] = useState(false);
  const [quickSaving, setQuickSaving] = useState(false);
  const [pasteSuccess, setPasteSuccess] = useState(false);
  const [classificationResult, setClassificationResult] = useState<string | null>(null);

  // Historique des 2 dernières activités
  interface Activity {
    type: 'paste' | 'drop' | 'move' | 'pdf';
    message: string;
    time: string;
  }
  const [recentActivities, setRecentActivities] = useState<Activity[]>([]);

  const addActivity = (type: Activity['type'], message: string) => {
    const time = new Date().toLocaleTimeString('fr-CH', { hour: '2-digit', minute: '2-digit' });
    setRecentActivities(prev => [{ type, message, time }, ...prev].slice(0, 2));
  };

  // Document attaché au contexte chatbot
  const [attachedDocument, setAttachedDocument] = useState<string | null>(null);

  // Clé pour forcer le refresh de FileTree
  const [fileTreeKey, setFileTreeKey] = useState(0);
  const refreshFiles = () => {
    // Refresh immédiat
    setFileTreeKey(k => k + 1);
    fetchActivity();
    // Refresh différé (Panda Portal met ~2s à indexer)
    setTimeout(() => setFileTreeKey(k => k + 1), 2500);
  };

  // Log d'activité persistant (backend)
  interface ActivityLog {
    timestamp: string;
    action: string;
    filename: string | null;
    details: string | null;
  }
  const [activityLog, setActivityLog] = useState<ActivityLog[]>([]);

  const fetchActivity = useCallback(async () => {
    if (!clientId) return;
    try {
      const res = await fetch(`/api/portal/clients/${clientId}/activity?limit=5`);
      if (res.ok) setActivityLog(await res.json());
    } catch {}
  }, [clientId]);

  useEffect(() => { fetchActivity(); }, [fetchActivity]);

  // Handler pour résumer tous les documents
  const handleSummarizeAll = async () => {
    const response = await fetch(`/api/portal/clients/${clientId}/summarize-all`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }
    });
    if (!response.ok) throw new Error('Erreur résumé global');
    return response.json();
  };

  // Handler pour déplacer un fichier
  const handleFileMove = async (from: string, to: string) => {
    try {
      const response = await fetch(`/api/portal/clients/${clientId}/docs/move`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ from, to })
      });
      if (response.ok) {
        const data = await response.json();
        refreshFiles();
        // Ajouter activité
        const fileName = from.split('/').pop() || from;
        const targetFolder = to.split('/').slice(0, -1).join('/') || 'racine';
        addActivity('move', `${fileName} → ${targetFolder}`);
        // Afficher feedback si PDF généré
        if (data.pdfGenerated) {
          setPasteSuccess(true);
          setClassificationResult('📄 PDF généré automatiquement');
          addActivity('pdf', `PDF créé: ${fileName}`);
          setTimeout(() => {
            setPasteSuccess(false);
            setClassificationResult(null);
          }, 3000);
        }
      } else {
        console.error('Erreur déplacement fichier');
      }
    } catch (err) {
      console.error('Erreur:', err);
    }
  };

  // Resizable splitter vertical (Gantt/Doc)
  const [ganttHeight, setGanttHeight] = useState(220); // pixels
  const [isResizingV, setIsResizingV] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const minGanttHeight = 120;
  const minDocHeight = 200;

  // Resizable splitter horizontal (Sidebar)
  const [sidebarWidth, setSidebarWidth] = useState(320); // pixels
  const [isResizingH, setIsResizingH] = useState(false);
  const minSidebarWidth = 250;
  const maxSidebarWidth = 500;

  useEffect(() => {
    if (clientId) {
      fetchConfig();
    }
  }, [clientId]);

  // Handle vertical resize (Gantt/Doc)
  const handleMouseDownV = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizingV(true);
  }, []);

  // Handle horizontal resize (Sidebar)
  const handleMouseDownH = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizingH(true);
  }, []);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      // Vertical resize
      if (isResizingV && containerRef.current) {
        const containerRect = containerRef.current.getBoundingClientRect();
        const newHeight = e.clientY - containerRect.top;
        const maxHeight = containerRect.height - minDocHeight;

        if (newHeight >= minGanttHeight && newHeight <= maxHeight) {
          setGanttHeight(newHeight);
        }
      }

      // Horizontal resize
      if (isResizingH) {
        const newWidth = e.clientX - 16; // 16px = padding left
        if (newWidth >= minSidebarWidth && newWidth <= maxSidebarWidth) {
          setSidebarWidth(newWidth);
        }
      }
    };

    const handleMouseUp = () => {
      setIsResizingV(false);
      setIsResizingH(false);
    };

    if (isResizingV || isResizingH) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = isResizingV ? 'row-resize' : 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizingV, isResizingH]);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`/api/portal/clients/${clientId}/config`);
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Client non trouvé');
        }
        throw new Error('Erreur chargement configuration');
      }
      const data = await response.json();
      setConfig(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
    } finally {
      setLoading(false);
    }
  };

  // Dropbox - Création rapide depuis paste → 03_Memoire_Vive/
  const handlePaste = async (e: React.ClipboardEvent) => {
    const text = e.clipboardData.getData('text');
    if (!text.trim() || !clientId) return;

    const now = new Date();
    const dateStr = now.toISOString().slice(0, 10);
    const timeStr = now.toTimeString().slice(0, 5).replace(':', 'h');

    // Nom du fichier dans 03_Memoire_Vive/
    const filename = `03_Memoire_Vive/${dateStr}_${timeStr}_note.md`;

    // Contenu avec header markdown
    const content = `# Note du ${dateStr} à ${timeStr.replace('h', ':')}

${text}
`;

    try {
      setQuickSaving(true);
      const response = await fetch(`/api/portal/clients/${clientId}/docs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: filename,
          content
        })
      });

      if (response.ok) {
        setPasteSuccess(true);
        setClassificationResult(`Note sauvegardée dans 03_Memoire_Vive`);
        addActivity('paste', `Note collée → Mémoire Vive`);
        setTimeout(() => { setPasteSuccess(false); setClassificationResult(null); }, 3000);
        setSelectedFile(filename);
        refreshFiles();
      }
    } catch (err) {
      console.error('Erreur sauvegarde rapide:', err);
    } finally {
      setQuickSaving(false);
    }
  };

  // Dropbox - Drag & Drop fichier → Classification IA automatique (texte + PDF)
  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length === 0 || !clientId) return;

    const file = files[0];
    const lowerName = file.name.toLowerCase();
    const isPdf = file.type === 'application/pdf' || lowerName.endsWith('.pdf');
    const isWord = file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      || file.type === 'application/msword'
      || lowerName.endsWith('.docx') || lowerName.endsWith('.doc');
    const isBinaryDoc = isPdf || isWord;

    if (isBinaryDoc) {
      // --- PDF/Word: lire en base64 et uploader via endpoint dédié ---
      const docLabel = isWord ? 'Word' : 'PDF';
      const reader = new FileReader();
      reader.onload = async (event) => {
        const dataUrl = event.target?.result as string;
        const base64 = dataUrl.split(',')[1]; // retirer le prefix data:...;base64,

        try {
          setQuickSaving(true);
          setClassificationResult(`Upload ${docLabel} en cours...`);

          const response = await fetch(`/api/portal/clients/${clientId}/upload-pdf`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ filename: file.name, base64 })
          });

          if (response.ok) {
            const data = await response.json();
            setPasteSuccess(true);
            setClassificationResult(`${docLabel} classifié dans ${data.folder}`);
            addActivity('drop', `${file.name} → ${data.folder}`);
            setSelectedFile(data.filename);
            refreshFiles();
            setTimeout(() => { setPasteSuccess(false); setClassificationResult(null); }, 4000);
          } else {
            const err = await response.json().catch(() => ({}));
            setClassificationResult(err.error || `Erreur upload ${docLabel}`);
            setTimeout(() => setClassificationResult(null), 3000);
          }
        } catch (err) {
          console.error(`Erreur upload ${docLabel}:`, err);
          setClassificationResult(`Erreur upload ${docLabel}`);
          setTimeout(() => setClassificationResult(null), 3000);
        } finally {
          setQuickSaving(false);
        }
      };
      reader.readAsDataURL(file);
    } else {
      // --- Fichier texte: classification IA standard ---
      const reader = new FileReader();
      reader.onload = async (event) => {
        const content = event.target?.result as string;
        const filename = file.name.endsWith('.md') ? file.name : `${file.name.replace(/\.[^/.]+$/, '')}.md`;

        try {
          setQuickSaving(true);
          setClassificationResult('Classification en cours...');

          const response = await fetch(`/api/portal/clients/${clientId}/classify`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ filename, content })
          });

          if (response.ok) {
            const data = await response.json();
            setPasteSuccess(true);
            const pdfInfo = data.pdfGenerated ? ' + PDF 📄' : '';
            setClassificationResult(`Classifié dans ${data.folder}${pdfInfo}`);
            addActivity('drop', `${filename} → ${data.folder}`);
            if (data.pdfGenerated) {
              addActivity('pdf', `PDF: ${filename.replace(/\.[^/.]+$/, '')}`);
            }
            setSelectedFile(data.filename);
            refreshFiles();
            setTimeout(() => { setPasteSuccess(false); setClassificationResult(null); }, 4000);
          } else {
            setClassificationResult('Erreur de classification');
            setTimeout(() => setClassificationResult(null), 3000);
          }
        } catch (err) {
          console.error('Erreur upload:', err);
          setClassificationResult('Erreur de classification');
          setTimeout(() => setClassificationResult(null), 3000);
        } finally {
          setQuickSaving(false);
        }
      };
      reader.readAsText(file);
    }
  };

  const handleCreateDocument = async () => {
    if (!newDocName.trim() || !clientId) return;

    const filename = newDocName.endsWith('.md') ? newDocName : `${newDocName}.md`;

    try {
      setCreating(true);
      const response = await fetch(`/api/portal/clients/${clientId}/docs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: filename,
          content: `# ${newDocName.replace('.md', '')}\n\nNouveau document créé le ${new Date().toLocaleDateString('fr-CH')}.\n`
        })
      });

      if (response.ok) {
        setShowCreateModal(false);
        setNewDocName('');
        setSelectedFile(filename);
        refreshFiles(); // Refresh sans reload complet
      }
    } catch (err) {
      console.error('Erreur création document:', err);
    } finally {
      setCreating(false);
    }
  };

  // Gradient basé sur la couleur primaire du client
  const getHeaderGradient = () => {
    const primary = config?.primaryColor || '#6366F1';
    const secondary = config?.secondaryColor || '#8B5CF6';
    return `linear-gradient(135deg, ${primary}, ${secondary})`;
  };

  if (!clientId) {
    return (
      <div className="h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 50%, #dfe3e8 100%)' }}>
        <div className="text-center p-8 bg-white/90 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/50">
          <div className="text-5xl mb-4">⚠️</div>
          <p className="text-gray-700 font-semibold">Client ID manquant</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 50%, #dfe3e8 100%)' }}>
        <div className="flex flex-col items-center gap-4 p-8 bg-white/90 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/50">
          <div className="w-12 h-12 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
          <span className="text-gray-700 font-semibold">Chargement...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 50%, #dfe3e8 100%)' }}>
        <div className="text-center p-8 bg-white/90 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/50">
          <div className="text-5xl mb-4">❌</div>
          <p className="text-red-500 font-semibold mb-4">{error}</p>
          <button
            onClick={fetchConfig}
            className="px-6 py-2.5 text-white font-bold rounded-xl transition-all transform hover:scale-105"
            style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col" style={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 50%, #dfe3e8 100%)' }}>
      {/* Header avec gradient client */}
      <header
        className="h-16 flex items-center px-6 shadow-lg flex-shrink-0"
        style={{ background: getHeaderGradient() }}
      >
        <div className="flex items-center gap-4">
          {config?.logo ? (
            <img src={config.logo} alt={config.name} className="h-10 w-10 rounded-xl shadow-md bg-white/20 p-1" />
          ) : (
            <div className="h-10 w-10 rounded-xl bg-white/20 flex items-center justify-center text-white text-xl font-bold">
              {config?.name?.charAt(0) || '?'}
            </div>
          )}
          <div>
            <h1 className="text-white font-bold text-lg tracking-tight">
              {config?.fullName || config?.name || clientId}
            </h1>
            {config?.type && (
              <p className="text-white/80 text-xs font-medium">{config.type}</p>
            )}
          </div>
        </div>
        {/* Espace réservé pour futures actions */}
        <div className="ml-auto flex items-center gap-3">
          <button
            className="p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
            title="Paramètres"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </button>
        </div>
      </header>

      {/* Main Layout */}
      <div className="flex-1 flex overflow-hidden p-4">
        {/* LEFT: FileTree + Dropbox */}
        <aside style={{ width: `${sidebarWidth}px` }} className="flex-shrink-0 flex flex-col gap-4">
          {/* FileTree Card */}
          <div className="flex-1 min-h-0 bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100">
            <div
              className="h-14 flex items-center px-4"
              style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
            >
              <span className="text-2xl mr-3">📁</span>
              <h2 className="text-white font-bold">Fichiers</h2>
            </div>
            <div className="h-[calc(100%-56px)]">
              <FileTree
                key={fileTreeKey}
                clientId={clientId}
                onSelectFile={setSelectedFile}
                selectedFile={selectedFile || undefined}
                onCreateDocument={() => setShowCreateModal(true)}
                onFileMove={handleFileMove}
                onSummarizeAll={handleSummarizeAll}
                onRefresh={refreshFiles}
              />
            </div>
          </div>

          {/* Dropbox Unique - Paste + Drag combinés */}
          <div className="h-[180px] flex-shrink-0 bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100">
            <div
              className="h-10 flex items-center justify-between px-3"
              style={{ background: 'linear-gradient(135deg, #f5af19 0%, #f12711 100%)' }}
            >
              <div className="flex items-center">
                <span className="text-lg mr-2">📥</span>
                <h3 className="text-white font-bold text-sm">Capture Rapide</h3>
              </div>
              {quickSaving && (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              )}
            </div>
            <div
              onPaste={handlePaste}
              onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
              tabIndex={0}
              className={`h-[calc(100%-40px)] flex flex-col items-center justify-center cursor-pointer group focus:outline-none transition-all duration-300 ${
                pasteSuccess ? '' : isDragging ? '' : 'hover:bg-amber-50/50'
              }`}
              style={{
                background: pasteSuccess
                  ? 'linear-gradient(135deg, rgba(74,222,128,0.15) 0%, rgba(34,197,94,0.15) 100%)'
                  : isDragging
                    ? 'linear-gradient(135deg, rgba(245,175,25,0.15) 0%, rgba(241,39,17,0.1) 100%)'
                    : undefined,
              }}
            >
              {quickSaving ? (
                <>
                  <div className="w-10 h-10 border-3 border-amber-500 border-t-transparent rounded-full animate-spin mb-2" />
                  <p className="text-xs font-semibold text-amber-600">
                    {classificationResult || 'Classification IA...'}
                  </p>
                </>
              ) : pasteSuccess ? (
                <>
                  <svg className="w-10 h-10 text-green-500 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-xs font-bold text-green-600">
                    {classificationResult || 'Sauvegardé!'}
                  </p>
                </>
              ) : (
                <div className="flex flex-col items-center w-full px-3">
                  {/* Zone de drop */}
                  <div className="flex flex-col items-center">
                    <svg
                      className={`w-6 h-6 transition-all duration-300 ${isDragging ? 'text-amber-500 scale-110' : 'text-amber-300 group-hover:text-amber-400'}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    <p className={`text-[10px] font-semibold mt-1 transition-colors ${isDragging ? 'text-amber-600' : 'text-gray-500 group-hover:text-amber-500'}`}>
                      {isDragging ? 'Déposez!' : 'Coller ou glisser'}
                    </p>
                  </div>

                  {/* Activités récentes */}
                  {recentActivities.length > 0 && (
                    <div className="w-full mt-2 pt-2 border-t border-gray-100">
                      <p className="text-[8px] text-gray-400 uppercase tracking-wide mb-1">Récent</p>
                      {recentActivities.map((activity, i) => (
                        <div key={i} className="flex items-center gap-1.5 text-[9px] text-gray-500 truncate">
                          <span className={`flex-shrink-0 ${
                            activity.type === 'paste' ? 'text-blue-500' :
                            activity.type === 'drop' ? 'text-amber-500' :
                            activity.type === 'pdf' ? 'text-red-500' :
                            'text-indigo-500'
                          }`}>
                            {activity.type === 'paste' ? '📋' :
                             activity.type === 'drop' ? '📥' :
                             activity.type === 'pdf' ? '📄' : '↔️'}
                          </span>
                          <span className="truncate flex-1">{activity.message}</span>
                          <span className="text-gray-300 flex-shrink-0">{activity.time}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </aside>

        {/* HORIZONTAL RESIZER */}
        <div
          onMouseDown={handleMouseDownH}
          className={`w-3 flex-shrink-0 flex items-center justify-center cursor-col-resize group transition-colors mx-1 ${
            isResizingH ? 'bg-indigo-100' : 'hover:bg-gray-100'
          }`}
        >
          <div className={`h-16 w-1.5 rounded-full transition-all ${
            isResizingH ? 'bg-indigo-500 h-24' : 'bg-gray-300 group-hover:bg-indigo-400 group-hover:h-20'
          }`} />
        </div>

        {/* RIGHT: Gantt + DocumentViewer avec splitter resizable */}
        <main ref={containerRef} className="flex-1 flex flex-col overflow-hidden">
          {/* TOP: Activity Log */}
          <section style={{ height: `${ganttHeight}px` }} className="flex-shrink-0">
            <div className="h-full bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100">
              <div
                className="h-12 flex items-center px-4"
                style={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' }}
              >
                <span className="text-xl mr-3">🕐</span>
                <h2 className="text-white font-bold text-sm">Activité récente</h2>
              </div>
              <div className="h-[calc(100%-48px)] overflow-y-auto px-4 py-2">
                {activityLog.length === 0 ? (
                  <div className="h-full flex items-center justify-center text-gray-400 text-sm">
                    Aucune activité enregistrée
                  </div>
                ) : (
                  <ul className="space-y-1.5">
                    {activityLog.map((entry, i) => {
                      const icons: Record<string, string> = { create: '📄', classify: '📂', move: '↔️', rename: '✏️', edit: '✏️', delete: '🗑️' };
                      const icon = icons[entry.action] || '📌';
                      const ago = (() => {
                        const diff = Date.now() - new Date(entry.timestamp).getTime();
                        const mins = Math.floor(diff / 60000);
                        if (mins < 1) return "à l'instant";
                        if (mins < 60) return `il y a ${mins}min`;
                        const hrs = Math.floor(mins / 60);
                        if (hrs < 24) return `il y a ${hrs}h`;
                        const days = Math.floor(hrs / 24);
                        return `il y a ${days}j`;
                      })();
                      const parts = entry.filename?.split('/') || [];
                      const shortName = parts.pop() || '';
                      const folderPath = parts.join('/') || '';
                      const dateStr = new Date(entry.timestamp).toLocaleDateString('fr-CH', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
                      return (
                        <li
                          key={i}
                          onClick={() => entry.filename && entry.action !== 'delete' && setSelectedFile(entry.filename)}
                          className={`flex items-start gap-2 text-xs py-1.5 border-b border-gray-50 last:border-0 rounded-lg px-2 -mx-2 transition-colors ${
                            entry.action !== 'delete' ? 'cursor-pointer hover:bg-indigo-50' : ''
                          }`}
                        >
                          <span className="text-base flex-shrink-0 mt-0.5">{icon}</span>
                          <div className="flex-1 min-w-0">
                            <span className="font-medium text-gray-700 truncate block">{shortName}</span>
                            {folderPath && (
                              <span className="inline-flex items-center gap-1 mt-0.5 px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-600 font-semibold text-[10px] truncate">
                                📁 {folderPath}
                              </span>
                            )}
                            {entry.details && <span className="text-gray-400 block mt-0.5">{entry.details}</span>}
                          </div>
                          <div className="flex-shrink-0 text-right">
                            <span className="text-gray-400 block whitespace-nowrap">{ago}</span>
                            <span className="text-gray-300 block whitespace-nowrap">{dateStr}</span>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
          </section>

          {/* VERTICAL RESIZER BAR */}
          <div
            onMouseDown={handleMouseDownV}
            className={`h-3 flex-shrink-0 flex items-center justify-center cursor-row-resize group transition-colors ${
              isResizingV ? 'bg-indigo-100' : 'hover:bg-gray-100'
            }`}
          >
            <div className={`w-16 h-1.5 rounded-full transition-all ${
              isResizingV ? 'bg-indigo-500 w-24' : 'bg-gray-300 group-hover:bg-indigo-400 group-hover:w-20'
            }`} />
          </div>

          {/* BOTTOM: DocumentViewer */}
          <section className="flex-1 min-h-0">
            <div className="h-full bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100">
              <DocumentViewer
                clientId={clientId}
                filename={selectedFile}
                attachedDocument={attachedDocument}
                onAttachDocument={setAttachedDocument}
              />
            </div>
          </section>
        </main>
      </div>

      {/* Modal Création Document */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden transform transition-all">
            <div
              className="p-6"
              style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
            >
              <h3 className="text-xl font-bold text-white">Nouveau document</h3>
              <p className="text-white/80 text-sm mt-1">Créer un document Markdown</p>
            </div>
            <div className="p-6">
              <input
                type="text"
                value={newDocName}
                onChange={(e) => setNewDocName(e.target.value)}
                placeholder="Nom du document..."
                className="w-full px-4 py-3 bg-gray-50 border-0 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 text-gray-700"
                autoFocus
                onKeyDown={(e) => e.key === 'Enter' && handleCreateDocument()}
              />
            </div>
            <div className="p-4 bg-gray-50 flex justify-end gap-3">
              <button
                onClick={() => { setShowCreateModal(false); setNewDocName(''); }}
                className="px-5 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-200 rounded-xl transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleCreateDocument}
                disabled={!newDocName.trim() || creating}
                className="px-5 py-2.5 text-sm font-bold text-white rounded-xl transition-all duration-300 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
              >
                {creating ? 'Création...' : 'Créer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Floating AI Assistant */}
      <FloatingAssistant
        clientId={clientId}
        onFileChange={refreshFiles}
        attachedDocument={attachedDocument}
        onDetachDocument={() => setAttachedDocument(null)}
      />
    </div>
  );
}
